/**
 * Branded accent components
 *
 * Collection of small UI elements that showcase the Penticton.dev
 * brand identity through Pacific blues and sunset accent colors.
 */

import {
  Chip,
  ChipProps,
  Divider,
  DividerProps,
  Badge,
  BadgeProps,
  Box,
  useTheme,
} from '@mui/material';
import { forwardRef } from 'react';

import { useAppTheme } from '../../app/providers/ThemeProvider';
import {
  getBrandChip,
  getBrandDivider,
  getBrandGradient,
  getGoldColor,
} from '../../theme/tokens';

// Brand Chip Component
export interface BrandChipProps extends Omit<ChipProps, 'color'> {
  /** Brand color variant */
  brandColor?: 'organizer' | 'member' | 'featured' | 'gold' | 'default';
  /** Custom gradient background */
  gradient?: boolean;
}

/**
 * Branded chip component with Pacific blues and sunset accents
 */
export const BrandChip = forwardRef<HTMLDivElement, BrandChipProps>(
  ({ brandColor = 'default', gradient = false, sx, ...props }, ref) => {
    const theme = useAppTheme();
    const muiTheme = useTheme();
    const mode = muiTheme.palette.mode;

    let chipStyles = {};

    if (brandColor !== 'default') {
      if (brandColor === 'gold') {
        // Special gold accent styling
        const goldMain = getGoldColor(700);
        const goldLight = getGoldColor(100);
        const goldDark = getGoldColor(800);

        chipStyles = {
          backgroundColor: mode === 'light' ? goldLight : `${goldMain}20`,
          color: mode === 'light' ? goldDark : getGoldColor(300),
          borderColor: goldMain,
          fontWeight: theme.customTokens.typography.fontWeight.semibold,
          border: `1px solid ${goldMain}`,
          '&:hover': {
            backgroundColor: mode === 'light' ? getGoldColor(200) : `${goldMain}30`,
            borderColor: goldDark,
          },
        };
      } else {
        const brandStyles = getBrandChip(brandColor, mode as 'light' | 'dark');

        if (brandColor === 'featured' && gradient) {
          chipStyles = {
            background:
              'background' in brandStyles
                ? brandStyles.background
                : getBrandGradient('primary', mode as 'light' | 'dark'),
            color: brandStyles.color,
            border: 'none',
            fontWeight: theme.customTokens.typography.fontWeight.semibold,
            textShadow: mode === 'light' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
          };
        } else {
          chipStyles = {
            backgroundColor:
              'backgroundColor' in brandStyles
                ? brandStyles.backgroundColor
                : brandStyles.color,
            color: brandStyles.color,
            borderColor:
              'borderColor' in brandStyles ? brandStyles.borderColor : 'transparent',
            fontWeight: theme.customTokens.typography.fontWeight.medium,
          };
        }
      }
    }

    return (
      <Chip
        ref={ref}
        sx={[
          {
            ...chipStyles,
            transition: `all ${theme.customTokens.motion.duration.fast} ${theme.customTokens.motion.easing.ease}`,
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: theme.customTokens.shadows.sm,
            },
          },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
        {...props}
      />
    );
  },
);

BrandChip.displayName = 'BrandChip';

// Brand Divider Component
export interface BrandDividerProps extends DividerProps {
  /** Brand color variant */
  brandColor?: 'brand' | 'accent' | 'gold' | 'default';
  /** Gradient style */
  gradient?: boolean;
}

/**
 * Branded divider with subtle brand colors
 */
export const BrandDivider = forwardRef<HTMLHRElement, BrandDividerProps>(
  ({ brandColor = 'default', gradient = false, sx, ...props }, ref) => {
    const muiTheme = useTheme();
    const mode = muiTheme.palette.mode;

    let dividerStyles = {};

    if (brandColor !== 'default') {
      if (brandColor === 'gold') {
        // Special gold accent styling
        if (gradient) {
          dividerStyles = {
            borderColor: 'transparent',
            background: `linear-gradient(90deg, transparent 0%, ${getGoldColor(600)} 50%, transparent 100%)`,
            height: '2px',
          };
        } else {
          dividerStyles = {
            borderColor: getGoldColor(mode === 'light' ? 600 : 400),
          };
        }
      } else {
        if (gradient) {
          dividerStyles = {
            borderColor: 'transparent',
            '&::before': {
              background: getBrandGradient('primary', mode as 'light' | 'dark'),
              height: '1px',
            },
          };
        } else {
          dividerStyles = {
            borderColor: getBrandDivider(brandColor, mode as 'light' | 'dark'),
          };
        }
      }
    }

    return (
      <Divider
        ref={ref}
        sx={[
          {
            ...dividerStyles,
          },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
        {...props}
      />
    );
  },
);

BrandDivider.displayName = 'BrandDivider';

// Brand Badge Component
export interface BrandBadgeProps extends BadgeProps {
  /** Brand color variant */
  brandColor?: 'organizer' | 'member' | 'featured' | 'gold';
  /** Gradient background */
  gradient?: boolean;
}

/**
 * Branded badge component with brand colors
 */
export const BrandBadge = forwardRef<HTMLSpanElement, BrandBadgeProps>(
  ({ brandColor = 'member', gradient = false, sx, ...props }, ref) => {
    const theme = useAppTheme();
    const muiTheme = useTheme();
    const mode = muiTheme.palette.mode;

    let badgeStyles = {};

    if (brandColor === 'gold') {
      // Special gold accent styling
      const goldMain = getGoldColor(700);
      const goldLight = getGoldColor(100);

      badgeStyles = {
        '& .MuiBadge-badge': {
          backgroundColor: goldMain,
          color: 'white',
          fontWeight: theme.customTokens.typography.fontWeight.semibold,
          border: `1px solid ${getGoldColor(800)}`,
          boxShadow: `0 2px 4px ${goldMain}40`,
        },
      };
    } else {
      const brandStyles = getBrandChip(brandColor, mode as 'light' | 'dark');

      if (gradient && brandColor === 'featured') {
        badgeStyles = {
          '& .MuiBadge-badge': {
            background:
              'background' in brandStyles
                ? brandStyles.background
                : getBrandGradient('primary', mode as 'light' | 'dark'),
            color: brandStyles.color,
            fontWeight: theme.customTokens.typography.fontWeight.semibold,
          },
        };
      } else {
        badgeStyles = {
          '& .MuiBadge-badge': {
            backgroundColor:
              'backgroundColor' in brandStyles
                ? brandStyles.backgroundColor
                : brandStyles.color,
            color: brandStyles.color,
            fontWeight: theme.customTokens.typography.fontWeight.medium,
          },
        };
      }
    }

    return (
      <Badge
        ref={ref}
        sx={[
          {
            ...badgeStyles,
          },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
        {...props}
      />
    );
  },
);

BrandBadge.displayName = 'BrandBadge';

// Brand Section Divider - Special decorative element
export interface BrandSectionDividerProps {
  /** Show brand accent dots */
  showAccents?: boolean;
  /** Custom width */
  width?: string | number;
  /** Use gold accent styling */
  goldAccent?: boolean;
}

/**
 * Decorative section divider with brand accents
 */
export const BrandSectionDivider = ({
  showAccents = true,
  width = '100%',
  goldAccent = false,
}: BrandSectionDividerProps) => {
  const theme = useAppTheme();
  const muiTheme = useTheme();
  const mode = muiTheme.palette.mode;

  const backgroundGradient = goldAccent
    ? `linear-gradient(90deg, transparent 0%, ${getGoldColor(600)} 50%, transparent 100%)`
    : getBrandGradient('primary', mode as 'light' | 'dark');

  return (
    <Box
      sx={{
        width,
        height: goldAccent ? '3px' : '2px',
        background: backgroundGradient,
        borderRadius: theme.customTokens.radii.full,
        position: 'relative',
        mx: 'auto',
      }}
    >
      {showAccents && (
        <>
          <Box
            sx={{
              position: 'absolute',
              left: '20%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: goldAccent ? '8px' : '6px',
              height: goldAccent ? '8px' : '6px',
              borderRadius: '50%',
              backgroundColor: goldAccent
                ? getGoldColor(800)
                : theme.customTokens.colors.sunset[mode === 'light' ? 500 : 400],
              boxShadow: goldAccent ? `0 2px 4px ${getGoldColor(700)}60` : 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              right: '20%',
              top: '50%',
              transform: 'translate(50%, -50%)',
              width: goldAccent ? '8px' : '6px',
              height: goldAccent ? '8px' : '6px',
              borderRadius: '50%',
              backgroundColor: goldAccent
                ? getGoldColor(600)
                : theme.customTokens.colors.pacific[mode === 'light' ? 600 : 400],
              boxShadow: goldAccent ? `0 2px 4px ${getGoldColor(700)}60` : 'none',
            }}
          />
        </>
      )}
    </Box>
  );
};

export default {
  BrandChip,
  BrandDivider,
  BrandBadge,
  BrandSectionDivider,
};
